import React, { useState, useEffect } from 'react';
import { standingsService } from '../../services/standingsService';
import { seasonManagementService } from '../../services/seasonManagementService';
import { scheduleService } from '../../services/scheduleService';
import { adminAuthService } from '../../services/adminAuth';
import { logsService } from '../../services/logsService';
import { supabase } from '../../services/supabase';
import { useSeasonContext } from '../../context/SeasonContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import GameDateEditor from './GameDateEditor';
import GameTeamEditor from './GameTeamEditor';
import PlayoffManager from './PlayoffManager';
import '../../styles/SeasonManager.css';
import '../../styles/SeasonManager-edit-button.css';

const SeasonManager = () => {
  const { reloadSeasons } = useSeasonContext();
  const { user } = useAdminAuth();
  const [activeSubTab, setActiveSubTab] = useState('seasons');
  const [seasons, setSeasons] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [scoreInputs, setScoreInputs] = useState({});

  const [formData, setFormData] = useState({
    seasonNumber: '',
    seasonName: '',
    seasonYear: new Date().getFullYear(),
    conferenceName: '',
    conferenceAbbr: '',
    teamName: '',
    teamAbbr: '',
    conferenceId: '',
    logoUrl: '',
    wins: 0,
    losses: 0,
    pointsFor: 0,
    pointsAgainst: 0,
    scheduleWeek: 1
  });
  const [logoFile, setLogoFile] = useState(null);
  const [editingConference, setEditingConference] = useState(null);
  const [showEditConferenceModal, setShowEditConferenceModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [canManageConferences, setCanManageConferences] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [showGameDateEditor, setShowGameDateEditor] = useState(false);
  const [editingGameForTeams, setEditingGameForTeams] = useState(null);
  const [showGameTeamEditor, setShowGameTeamEditor] = useState(false);

  useEffect(() => {
    loadData();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        const canManage = await adminAuthService.userCanManageConferences(user.id);
        setCanManageConferences(canManage);
      }
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [seasonData, confData, teamData] = await Promise.all([
        standingsService.getAllSeasons(),
        standingsService.getConferences(),
        seasonManagementService.getAllTeams()
      ]);

      setSeasons(seasonData);
      setConferences(confData);
      setTeams(teamData);

      if (seasonData.length > 0) {
        const activeSeason = seasonData.find(s => s.is_active) || seasonData[0];
        setSelectedSeason(activeSeason);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Year') || name.includes('wins') || name.includes('losses') || name.includes('points') 
        ? parseInt(value) || 0 
        : value
    }));
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.seasonNumber || !formData.seasonName) {
      setError('Season number and name are required');
      return;
    }

    setLoading(true);
    try {
      const newSeason = await seasonManagementService.createSeason(
        parseInt(formData.seasonNumber),
        formData.seasonName,
        formData.seasonYear
      );

      setSeasons(prev => [...prev, newSeason]);
      setSelectedSeason(newSeason);
      setFormData(prev => ({
        ...prev,
        seasonNumber: '',
        seasonName: '',
        seasonYear: new Date().getFullYear()
      }));
      await reloadSeasons();
      setSuccess('Season created successfully');
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'SEASON_CREATE',
          {
            season_number: parseInt(formData.seasonNumber),
            season_name: formData.seasonName,
            season_year: formData.seasonYear
          },
          formData.seasonName
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to create season');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConference = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.conferenceName || !formData.conferenceAbbr) {
      setError('Conference name and abbreviation are required');
      return;
    }

    setLoading(true);
    try {
      const newConf = await seasonManagementService.createConference(
        formData.conferenceName,
        formData.conferenceAbbr
      );

      setConferences(prev => [...prev, newConf]);
      setFormData(prev => ({
        ...prev,
        conferenceName: '',
        conferenceAbbr: ''
      }));
      setSuccess('Conference created successfully');
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'CONFERENCE_CREATE',
          {
            conference_name: formData.conferenceName,
            conference_abbr: formData.conferenceAbbr
          }
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to create conference');
    } finally {
      setLoading(false);
    }
  };

  const handleEditConference = (conference) => {
    setEditingConference(conference);
    setFormData(prev => ({
      ...prev,
      conferenceName: conference.name,
      conferenceAbbr: conference.abbreviation
    }));
    setShowEditConferenceModal(true);
  };

  const handleUpdateConference = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.conferenceName || !formData.conferenceAbbr) {
      setError('Conference name and abbreviation are required');
      return;
    }

    setLoading(true);
    try {
      const updated = await seasonManagementService.updateConference(
        editingConference.id,
        {
          name: formData.conferenceName,
          abbreviation: formData.conferenceAbbr
        }
      );

      setConferences(prev =>
        prev.map(conf => conf.id === editingConference.id ? updated : conf)
      );
      setSuccess('Conference updated successfully');
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'CONFERENCE_UPDATE',
          {
            conference_id: editingConference.id,
            new_name: formData.conferenceName,
            new_abbr: formData.conferenceAbbr
          }
        );
      }
      
      setShowEditConferenceModal(false);
      setEditingConference(null);
      setFormData(prev => ({
        ...prev,
        conferenceName: '',
        conferenceAbbr: ''
      }));
    } catch (err) {
      setError(err.message || 'Failed to update conference');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConference = async (conferenceId) => {
    if (window.confirm('Are you sure you want to delete this conference?')) {
      setError(null);
      setSuccess(null);
      setLoading(true);
      try {
        const deletedConf = conferences.find(c => c.id === conferenceId);
        await seasonManagementService.deleteConference(conferenceId);
        setConferences(prev => prev.filter(conf => conf.id !== conferenceId));
        
        if (user) {
          await logsService.createLog(
            user.id,
            user.email,
            'CONFERENCE_DELETE',
            {
              conference_id: conferenceId,
              conference_name: deletedConf?.name,
              conference_abbr: deletedConf?.abbreviation
            }
          );
        }
        
        setSuccess('Conference deleted successfully');
      } catch (err) {
        setError(err.message || 'Failed to delete conference');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setFormData(prev => ({
        ...prev,
        logoUrl: file.name
      }));
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.teamName || !formData.teamAbbr || !formData.conferenceId) {
      setError('Team name, abbreviation, and conference are required');
      return;
    }

    setLoading(true);
    try {
      let logoUrl = formData.logoUrl;

      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${formData.teamAbbr.toLowerCase()}-${Date.now()}.${fileExt}`;
        
        const { data, error: uploadError } = await supabase.storage
          .from('team-logos')
          .upload(fileName, logoFile);

        if (uploadError) throw uploadError;
        
        const { data: urlData } = supabase.storage
          .from('team-logos')
          .getPublicUrl(fileName);
        
        logoUrl = urlData.publicUrl;
        setLogoFile(null);
      }

      const newTeam = await seasonManagementService.createTeam(
        formData.teamName,
        formData.teamAbbr,
        parseInt(formData.conferenceId),
        logoUrl || null
      );

      setTeams(prev => [...prev, newTeam]);
      
      if (selectedSeason) {
        await seasonManagementService.addTeamToStandings(selectedSeason.id, newTeam.id);
      }
      
      setFormData(prev => ({
        ...prev,
        teamName: '',
        teamAbbr: '',
        conferenceId: '',
        logoUrl: ''
      }));
      
      if (user) {
        const selectedConf = conferences.find(c => c.id === parseInt(formData.conferenceId));
        await logsService.createLog(
          user.id,
          user.email,
          'TEAM_CREATE',
          {
            team_name: formData.teamName,
            team_abbr: formData.teamAbbr,
            conference: selectedConf?.name,
            has_logo: !!logoFile
          }
        );
      }
      
      setSuccess('Team created successfully');
    } catch (err) {
      setError(err.message || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeamToSeason = async (teamId) => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    setLoading(true);
    try {
      await seasonManagementService.addTeamToStandings(
        selectedSeason.id,
        teamId,
        formData.wins,
        formData.losses,
        formData.pointsFor,
        formData.pointsAgainst
      );

      setSuccess('Team added to season standings');
      setFormData(prev => ({
        ...prev,
        wins: 0,
        losses: 0,
        pointsFor: 0,
        pointsAgainst: 0
      }));

      if (selectedSeason) {
        const updated = await seasonManagementService.getTeamsForSeason(selectedSeason.id);
        setTeams(updated);
      }
    } catch (err) {
      setError(err.message || 'Failed to add team to season');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;

    setLoading(true);
    try {
      const deletedTeam = teams.find(t => t.id === teamId);
      await seasonManagementService.deleteTeam(teamId);
      setTeams(prev => prev.filter(t => t.id !== teamId));
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'TEAM_DELETE',
          {
            team_id: teamId,
            team_name: deletedTeam?.name,
            team_abbr: deletedTeam?.abbreviation
          }
        );
      }
      
      setSuccess('Team deleted successfully');
    } catch (err) {
      setError(err.message || 'Failed to delete team');
    } finally {
      setLoading(false);
    }
  };

  const weekAlreadyScheduled = (weekNum) => {
    return schedule.some(game => game.week === weekNum);
  };

  const handleGenerateSchedule = async (weekNum = 1) => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    if (weekAlreadyScheduled(weekNum)) {
      setError(`Week ${weekNum} has already been scheduled. Delete the existing week first before generating a new schedule.`);
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const teamStandings = await standingsService.getStandingsBySeason(selectedSeason.id);
      
      if (!teamStandings || teamStandings.length === 0) {
        setError('No teams added to this season. Add teams first, then generate schedule.');
        setLoading(false);
        return;
      }

      const result = await scheduleService.generateSchedule(selectedSeason.id, weekNum);
      setSchedule(result.games);
      setSuccess(result.message);
      setSelectedWeek(weekNum);
    } catch (err) {
      setError(err.message || 'Failed to generate schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateSmartSchedule = async () => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    const confirmRegenerate = window.confirm(
      'This will delete the existing schedule and regenerate a fair schedule that avoids rematches and balances team strength. Continue?'
    );

    if (!confirmRegenerate) {
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const teamStandings = await standingsService.getStandingsBySeason(selectedSeason.id);
      
      if (!teamStandings || teamStandings.length === 0) {
        setError('No teams added to this season. Add teams first, then generate schedule.');
        setLoading(false);
        return;
      }

      const result = await scheduleService.regenerateSmartSchedule(selectedSeason.id);
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);
      setSchedule(games);
      setSuccess(result.message);
      setSelectedWeek(1);
    } catch (err) {
      setError(err.message || 'Failed to regenerate schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRemainingWeeks = async () => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    const confirmGenerate = window.confirm(
      'This will generate fair matchups for all remaining weeks, avoiding rematches and balancing team strength. Current weeks will be preserved. Continue?'
    );

    if (!confirmGenerate) {
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const teamStandings = await standingsService.getStandingsBySeason(selectedSeason.id);
      
      if (!teamStandings || teamStandings.length === 0) {
        setError('No teams added to this season. Add teams first.');
        setLoading(false);
        return;
      }

      const result = await scheduleService.generateRemainingWeeksFairly(selectedSeason.id);
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);
      setSchedule(games);
      setSuccess(result.message);
    } catch (err) {
      setError(err.message || 'Failed to generate remaining weeks');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSchedule = async () => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    setLoading(true);
    try {
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);
      setSchedule(games);
      if (games.length > 0) {
        setSelectedWeek(1);
      }
    } catch (err) {
      setError(err.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadScores = async () => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    setLoading(true);
    try {
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);
      setSchedule(games);
      const inputs = {};
      games.forEach(game => {
        if (game.game_scores) {
          inputs[game.id] = {
            team1Score: game.game_scores.team1_score ?? '',
            team2Score: game.game_scores.team2_score ?? ''
          };
        } else {
          inputs[game.id] = { team1Score: '', team2Score: '' };
        }
      });
      setScoreInputs(inputs);
    } catch (err) {
      setError(err.message || 'Failed to load scores');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (gameId, field, value) => {
    setScoreInputs(prev => ({
      ...prev,
      [gameId]: {
        ...prev[gameId],
        [field]: value === '' ? '' : parseInt(value) || 0
      }
    }));
  };

  const handleSubmitScore = async (gameId) => {
    const scores = scoreInputs[gameId];
    if (!scores) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const team1Score = scores.team1Score === '' ? 0 : scores.team1Score;
      const team2Score = scores.team2Score === '' ? 0 : scores.team2Score;
      await scheduleService.updateGameScore(gameId, team1Score, team2Score);
      setSuccess('Score updated successfully');
      await handleLoadScores();
    } catch (err) {
      setError(err.message || 'Failed to submit score');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSeasonStandings = async () => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    if (!window.confirm(`Are you sure you want to reset all standings for ${selectedSeason.name} to 0-0? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await seasonManagementService.resetSeasonStandings(selectedSeason.id);
      setSuccess(`All standings for ${selectedSeason.name} have been reset to 0-0-0-0`);
      setScoreInputs({});
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to reset standings');
      console.error('Error resetting standings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRankings = async () => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await standingsService.updateRankingsBySeason(selectedSeason.id);
      setSuccess(`Rankings for ${selectedSeason.name} have been updated successfully`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update rankings');
      console.error('Error updating rankings:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasStartedWeeks = () => {
    const now = new Date();
    return schedule.some(game => {
      if (game.is_completed) return true;
      if (game.scheduled_date) {
        const gameDate = new Date(game.scheduled_date);
        return gameDate <= now;
      }
      return false;
    });
  };

  const weekHasStarted = (weekNum) => {
    const now = new Date();
    return schedule.some(game => {
      if (game.week !== weekNum) return false;
      if (game.is_completed) return true;
      if (game.scheduled_date) {
        const gameDate = new Date(game.scheduled_date);
        return gameDate <= now;
      }
      return false;
    });
  };

  const gameHasStarted = (game) => {
    if (game.is_completed) return true;
    if (game.scheduled_date) {
      const now = new Date();
      const gameDate = new Date(game.scheduled_date);
      return gameDate <= now;
    }
    return false;
  };

  const handleDeleteWeek = async (weekNum) => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    if (weekHasStarted(weekNum)) {
      setError(`Cannot delete Week ${weekNum}: This week has already started or been completed.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete Week ${weekNum}? All games in this week will be removed.`)) {
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const result = await scheduleService.deleteWeek(selectedSeason.id, weekNum);
      setSchedule(prev => prev.filter(game => game.week !== weekNum));
      setSuccess(result.message);
      if (selectedWeek === weekNum) {
        const maxWeek = Math.max(...schedule.filter(g => g.week !== weekNum).map(g => g.week), 1);
        setSelectedWeek(Math.min(selectedWeek, maxWeek));
      }
    } catch (err) {
      setError(err.message || `Failed to delete Week ${weekNum}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!selectedSeason) {
      setError('Please select a season first');
      return;
    }

    if (hasStartedWeeks()) {
      setError('Cannot delete schedule: One or more weeks have already started or been completed. Please delete individual games or contact support.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete the entire schedule?')) {
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const result = await scheduleService.deleteSchedule(selectedSeason.id);
      setSchedule([]);
      setSuccess(result.message);
    } catch (err) {
      setError(err.message || 'Failed to delete schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchTeams = async (gameId) => {
    setError(null);
    setSuccess(null);

    setLoading(true);
    try {
      await scheduleService.switchGameTeams(gameId);
      const updatedSchedule = schedule.map(game => 
        game.id === gameId 
          ? {
              ...game,
              team1_id: game.team2_id,
              team2_id: game.team1_id,
              team1: game.team2,
              team2: game.team1
            }
          : game
      );
      setSchedule(updatedSchedule);
      setSuccess(`Teams switched for this game`);
    } catch (err) {
      setError(err.message || 'Failed to switch teams');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTeamEditor = (game) => {
    setEditingGameForTeams(game);
    setShowGameTeamEditor(true);
  };

  const handleToggleSeason = async (seasonId, isActive) => {
    try {
      if (!isActive) {
        const { error: deactivateError } = await supabase
          .from('seasons')
          .update({ is_active: false })
          .neq('id', seasonId);

        if (deactivateError) throw deactivateError;
      }

      const { error } = await supabase
        .from('seasons')
        .update({ is_active: !isActive })
        .eq('id', seasonId);

      if (error) throw error;

      const updatedSeasons = seasons.map(s =>
        s.id === seasonId 
          ? { ...s, is_active: !isActive }
          : { ...s, is_active: false }
      );
      setSeasons(updatedSeasons);
      
      if (!isActive) {
        const activatedSeason = updatedSeasons.find(s => s.id === seasonId);
        setSelectedSeason(activatedSeason);
        try {
          await seasonManagementService.addAllTeamsToSeason(seasonId);
          const updated = await seasonManagementService.getTeamsForSeason(seasonId);
          setTeams(updated);
        } catch (err) {
          console.error('Error adding teams to season:', err);
        }
      }
      
      alert(`Season ${!isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling season:', error);
      alert(`Failed to ${!isActive ? 'activate' : 'deactivate'} season`);
    }
  };

  const handleDeleteSeason = async (seasonId) => {
    if (window.confirm('Are you sure you want to delete this season? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('seasons')
          .delete()
          .eq('id', seasonId);

        if (error) throw error;

        const deletedSeason = seasons.find(s => s.id === seasonId);
        setSeasons(seasons.filter(s => s.id !== seasonId));
        
        if (user) {
          await logsService.createLog(
            user.id,
            user.email,
            'SEASON_DELETE',
            {
              season_id: seasonId,
              season_name: deletedSeason?.name,
              season_number: deletedSeason?.season_number
            }
          );
        }
        
        alert('Season deleted successfully');
      } catch (error) {
        console.error('Error deleting season:', error);
        alert('Failed to delete season');
      }
    }
  };

  return (
    <>
    <div className="season-manager">
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="season-subtabs">
        <button
          className={`subtab ${activeSubTab === 'seasons' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('seasons')}
        >
          Seasons
        </button>
        <button
          className={`subtab ${activeSubTab === 'conferences' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('conferences')}
        >
          Conferences
        </button>
        <button
          className={`subtab ${activeSubTab === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('teams')}
        >
          Teams
        </button>
        <button
          className={`subtab ${activeSubTab === 'schedule' ? 'active' : ''}`}
          onClick={() => {
            setActiveSubTab('schedule');
            handleLoadSchedule();
          }}
        >
          Schedule
        </button>
        <button
          className={`subtab ${activeSubTab === 'scores' ? 'active' : ''}`}
          onClick={() => {
            setActiveSubTab('scores');
            handleLoadScores();
          }}
        >
          Scores
        </button>
        <button
          className={`subtab ${activeSubTab === 'playoffs' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('playoffs')}
        >
          Playoffs
        </button>
      </div>

      {activeSubTab === 'seasons' && (
        <div className="season-section">
          <h3>Create New Season</h3>
          <form onSubmit={handleCreateSeason} className="form-group">
            <div className="form-row">
              <div className="form-field">
                <label>Season Number</label>
                <input
                  type="number"
                  name="seasonNumber"
                  value={formData.seasonNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., 9"
                />
              </div>
              <div className="form-field">
                <label>Season Name</label>
                <input
                  type="text"
                  name="seasonName"
                  value={formData.seasonName}
                  onChange={handleInputChange}
                  placeholder="e.g., Season 9"
                />
              </div>
              <div className="form-field">
                <label>Year</label>
                <input
                  type="number"
                  name="seasonYear"
                  value={formData.seasonYear}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Creating...' : 'Create Season'}
            </button>
          </form>

          <h3>Existing Seasons</h3>
          <div className="items-list">
            {seasons.length === 0 ? (
              <p>No seasons created yet.</p>
            ) : (
              seasons.map(season => (
                <div key={season.id} className="item-card">
                  <div className="item-info">
                    <h4>{season.name}</h4>
                    <p>Number: {season.season_number} | Year: {season.year}</p>
                    <p>Status: {season.is_active ? '🟢 Active' : '⚪ Inactive'}</p>
                    <div className="season-actions">
                      <button
                        onClick={async () => {
                          setSelectedSeason(season);
                          try {
                            await seasonManagementService.addAllTeamsToSeason(season.id);
                            const updated = await seasonManagementService.getTeamsForSeason(season.id);
                            setTeams(updated);
                          } catch (err) {
                            console.error('Error adding teams to season:', err);
                          }
                        }}
                        className={`btn-primary ${selectedSeason?.id === season.id ? 'active' : ''}`}
                      >
                        {selectedSeason?.id === season.id ? '✓ Selected' : 'Select'}
                      </button>
                      <button
                        onClick={() => handleToggleSeason(season.id, season.is_active)}
                        className="btn-secondary"
                      >
                        {season.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteSeason(season.id)}
                        className="btn-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'conferences' && (
        <div className="season-section">
          <h3>Create New Conference</h3>
          <form onSubmit={handleCreateConference} className="form-group">
            <div className="form-row">
              <div className="form-field">
                <label>Conference Name</label>
                <input
                  type="text"
                  name="conferenceName"
                  value={formData.conferenceName}
                  onChange={handleInputChange}
                  placeholder="e.g., Conference UCFL"
                />
              </div>
              <div className="form-field">
                <label>Abbreviation</label>
                <input
                  type="text"
                  name="conferenceAbbr"
                  value={formData.conferenceAbbr}
                  onChange={handleInputChange}
                  placeholder="e.g., UCFL"
                  maxLength="10"
                />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Creating...' : 'Create Conference'}
            </button>
          </form>

          <h3>Existing Conferences</h3>
          <div className="items-list">
            {conferences.length === 0 ? (
              <p>No conferences created yet.</p>
            ) : (
              conferences.map(conf => (
                <div key={conf.id} className="item-card">
                  <div className="item-info">
                    <h4>{conf.name}</h4>
                    <p>Abbreviation: {conf.abbreviation}</p>
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEditConference(conf)}
                      disabled={loading || !canManageConferences}
                      title={canManageConferences ? 'Edit this conference' : 'You do not have permission to edit conferences'}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteConference(conf.id)}
                      disabled={loading || !canManageConferences}
                      title={canManageConferences ? 'Delete this conference' : 'You do not have permission to delete conferences'}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'teams' && (
        <div className="season-section">
          <h3>Create New Team</h3>
          <form onSubmit={handleCreateTeam} className="form-group">
            <div className="form-row">
              <div className="form-field">
                <label>Team Name</label>
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleInputChange}
                  placeholder="e.g., Georgia Bulldogs"
                />
              </div>
              <div className="form-field">
                <label>Abbreviation</label>
                <input
                  type="text"
                  name="teamAbbr"
                  value={formData.teamAbbr}
                  onChange={handleInputChange}
                  placeholder="e.g., UGA"
                  maxLength="10"
                />
              </div>
              <div className="form-field">
                <label>Conference</label>
                <select
                  name="conferenceId"
                  value={formData.conferenceId}
                  onChange={handleInputChange}
                >
                  <option value="">Select a conference</option>
                  {conferences.map(conf => (
                    <option key={conf.id} value={conf.id}>
                      {conf.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Team Logo (Upload Image)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                />
              </div>
              <div className="form-field">
                <label>Or Paste Logo URL</label>
                <input
                  type="text"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, logoUrl: e.target.value }));
                    setLogoFile(null);
                  }}
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Creating...' : 'Create Team'}
            </button>
          </form>

          <h3>Existing Teams</h3>
          <div className="items-list">
            {teams.length === 0 ? (
              <p>No teams created yet.</p>
            ) : (
              teams.map(team => (
                <div key={team.id} className="item-card">
                  <div className="item-info">
                    <h4>{team.name}</h4>
                    <p>Abbreviation: {team.abbreviation}</p>
                    {team.conferences && (
                      <p>Conference: {team.conferences.name}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteTeam(team.id)}
                    className="btn btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'schedule' && (
        <div className="season-section">
          <h3>Generate Schedule for {selectedSeason?.name}</h3>
          <div className="form-group">
            <div className="form-row">
              <div className="form-field">
                <label>Select Week to Generate</label>
                <input
                  type="number"
                  min="1"
                  max={selectedSeason ? (
                    Math.max(...schedule.map(g => g.week), 0) + 15
                  ) : 17}
                  value={formData.scheduleWeek}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduleWeek: parseInt(e.target.value) || 1 }))}
                  placeholder="Week 1"
                />
              </div>
            </div>
            <div className="schedule-actions">
              <button
                onClick={() => handleGenerateSchedule(formData.scheduleWeek)}
                disabled={loading || !selectedSeason || weekAlreadyScheduled(formData.scheduleWeek)}
                className="btn btn-primary"
                title={weekAlreadyScheduled(formData.scheduleWeek) ? `Week ${formData.scheduleWeek} has already been scheduled` : 'Generate schedule for this week'}
              >
                Generate Week {formData.scheduleWeek} Schedule
              </button>
              <button
                onClick={handleGenerateRemainingWeeks}
                disabled={loading || !selectedSeason || schedule.length === 0}
                className="btn btn-success"
                title="Generate remaining weeks fairly, avoiding rematches and balancing team strength"
              >
                Generate Fair Remaining Weeks
              </button>
              <button
                onClick={handleRegenerateSmartSchedule}
                disabled={loading || !selectedSeason}
                className="btn btn-success"
                title="Regenerate entire schedule (deletes all existing games) avoiding rematches and balancing team strength"
              >
                Regenerate Full Fair Schedule
              </button>
              <button
                onClick={handleDeleteSchedule}
                disabled={loading || !selectedSeason || schedule.length === 0 || hasStartedWeeks()}
                className="btn btn-danger"
                title={hasStartedWeeks() ? 'Cannot delete schedule: weeks have already started' : 'Delete entire schedule'}
              >
                Delete Schedule
              </button>
            </div>
          </div>

          {schedule.length > 0 && (
            <div className="schedule-container">
              <div className="week-selector">
                <label>Select Week:</label>
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                  className="form-control"
                >
                  {Array.from({ length: Math.max(...schedule.map(g => g.week), 1) }, (_, i) => i + 1).map(w => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
                <button
                  onClick={() => handleDeleteWeek(selectedWeek)}
                  disabled={loading || !selectedSeason || weekHasStarted(selectedWeek)}
                  className="btn btn-danger btn-sm"
                  title={weekHasStarted(selectedWeek) ? `Cannot delete Week ${selectedWeek}: it has already started` : `Delete Week ${selectedWeek}`}
                >
                  Delete Week
                </button>
              </div>

              <div className="schedule-grid">
                {schedule.filter(g => g.week === selectedWeek).map(game => (
                  <div key={game.id} className="schedule-card">
                    <div className="schedule-matchup">
                      <div className="team-info">
                        <h4>{game.team1?.name}</h4>
                        <p className="team-abbr">{game.team1?.abbreviation}</p>
                      </div>
                      <div className="vs">vs</div>
                      <div className="team-info">
                        <h4>{game.team2?.name}</h4>
                        <p className="team-abbr">{game.team2?.abbreviation}</p>
                      </div>
                    </div>
                    <div className="schedule-details">
                      <div className="schedule-card-header">
                        <div>
                          <p>Week {game.week}</p>
                          {game.scheduled_date && (
                            <p><strong>{new Date(game.scheduled_date).toLocaleDateString()} {new Date(game.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong></p>
                          )}
                          {!game.scheduled_date && (
                            <p><em style={{ color: '#999' }}>No date set</em></p>
                          )}
                        </div>
                        <div className="schedule-actions">
                          <button
                            className="btn-edit-date"
                            onClick={() => {
                              setEditingGame(game);
                              setShowGameDateEditor(true);
                            }}
                            disabled={loading || gameHasStarted(game)}
                            title={gameHasStarted(game) ? 'Cannot edit: game has started or been completed' : 'Set game date and time'}
                          >
                            📅
                          </button>
                          <button
                            className="btn-switch-teams"
                            onClick={() => handleSwitchTeams(game.id)}
                            disabled={loading || gameHasStarted(game)}
                            title={gameHasStarted(game) ? 'Cannot switch: game has started or been completed' : 'Switch teams for this game'}
                          >
                            🔄
                          </button>
                          <button
                            className="btn-edit-teams"
                            onClick={() => handleOpenTeamEditor(game)}
                            disabled={loading || gameHasStarted(game)}
                            title={gameHasStarted(game) ? 'Cannot change teams: game has started or been completed' : 'Change teams'}
                          >
                            ⚔️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {schedule.length === 0 && (
            <div className="empty-state">
              <p>No schedule generated yet. Click "Generate New Schedule" to create one.</p>
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'scores' && (
        <div className="season-section">
          <div className="section-header">
            <h3>Game Scores for {selectedSeason?.name}</h3>
            <div className="button-group">
              <button
                onClick={handleUpdateRankings}
                disabled={loading || !selectedSeason}
                className="btn btn-primary"
                title="Recalculate and update all rankings based on W%, DIFF, and PF"
              >
                Update Rankings
              </button>
              <button
                onClick={handleResetSeasonStandings}
                disabled={loading || !selectedSeason}
                className="btn btn-warning"
                title="Reset all standings for this season to 0-0-0-0"
              >
                Reset Season Standings
              </button>
            </div>
          </div>
          
          {schedule.length > 0 && (
            <div className="scores-container">
              <div className="week-selector">
                <label>Select Week:</label>
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                  className="form-control"
                >
                  {Array.from({ length: Math.max(...schedule.map(g => g.week), 1) }, (_, i) => i + 1).map(w => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
                <button
                  onClick={() => handleDeleteWeek(selectedWeek)}
                  disabled={loading || !selectedSeason || weekHasStarted(selectedWeek)}
                  className="btn btn-danger btn-sm"
                  title={weekHasStarted(selectedWeek) ? `Cannot delete Week ${selectedWeek}: it has already started` : `Delete Week ${selectedWeek}`}
                >
                  Delete Week
                </button>
              </div>

              <div className="scores-grid">
                {schedule.filter(g => g.week === selectedWeek).map(game => (
                  <div key={game.id} className="score-card">
                    <div className="score-header">
                      <h4>{game.team1?.name} vs {game.team2?.name}</h4>
                      <p className="score-abbr">{game.team1?.abbreviation} vs {game.team2?.abbreviation}</p>
                    </div>

                    <div className="score-inputs-container">
                      <div className="score-input-group">
                        <label>{game.team1?.abbreviation}</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={scoreInputs[game.id]?.team1Score ?? ''}
                          onChange={(e) => handleScoreChange(game.id, 'team1Score', e.target.value)}
                        />
                      </div>
                      <div className="score-vs">vs</div>
                      <div className="score-input-group">
                        <label>{game.team2?.abbreviation}</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={scoreInputs[game.id]?.team2Score ?? ''}
                          onChange={(e) => handleScoreChange(game.id, 'team2Score', e.target.value)}
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => handleSubmitScore(game.id)}
                      disabled={loading}
                      className="btn btn-success btn-sm"
                    >
                      Submit Score
                    </button>

                    {game.is_completed && (
                      <div className="score-status">
                        <p className="completed-badge">✓ Completed</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {schedule.length === 0 && (
            <div className="empty-state">
              <p>No schedule available. Please generate a schedule first.</p>
            </div>
          )}
        </div>
      )}

      {showEditConferenceModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Conference</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowEditConferenceModal(false);
                  setEditingConference(null);
                  setFormData(prev => ({
                    ...prev,
                    conferenceName: '',
                    conferenceAbbr: ''
                  }));
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateConference} className="form-group">
              <div className="form-row">
                <div className="form-field">
                  <label>Conference Name</label>
                  <input
                    type="text"
                    name="conferenceName"
                    value={formData.conferenceName}
                    onChange={handleInputChange}
                    placeholder="e.g., Conference UCFL"
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Abbreviation</label>
                  <input
                    type="text"
                    name="conferenceAbbr"
                    value={formData.conferenceAbbr}
                    onChange={handleInputChange}
                    placeholder="e.g., UCFL"
                    maxLength="10"
                    required
                  />
                </div>
              </div>
              <button type="submit" disabled={loading}>
                {loading ? 'Updating...' : 'Update Conference'}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeSubTab === 'playoffs' && selectedSeason && (
        <PlayoffManager selectedSeason={selectedSeason} />
      )}
    </div>
    <GameDateEditor
      game={editingGame}
      isOpen={showGameDateEditor}
      onClose={() => {
        setShowGameDateEditor(false);
        setEditingGame(null);
      }}
      onSave={() => {
        handleLoadSchedule();
      }}
    />
    <GameTeamEditor
      game={editingGameForTeams}
      isOpen={showGameTeamEditor}
      onClose={() => {
        setShowGameTeamEditor(false);
        setEditingGameForTeams(null);
      }}
      onSave={() => {
        handleLoadSchedule();
      }}
      seasonId={selectedSeason?.id}
      teams={teams}
    />
    </>
  );
};

export default SeasonManager;
